/**
 * Confidence-unit normalisation — the single mapping point (P3 §3).
 *
 * The canonical engine confidence (`LineResolution.confidence`,
 * `IntentClassification.confidence`, `ResolveResponse.confidence` from the live
 * API) is a probability on **0..1**. The DS `Meter` / `ConfidenceMeter` and the
 * queue's `%` column consume a percentage on **0..100**. Rendering a raw 0.94
 * into a meter would read as "0.94%".
 *
 * Every live confidence value crosses into the UI through this helper, so the
 * ×100 conversion lives in exactly one place. The mock fixtures are already
 * authored on 0..100 (see `lib/mock/types.ts`), so they can be fed through this
 * too without double-scaling: any value already above 1 is treated as an
 * existing percentage and passed through untouched.
 */

/**
 * Normalise a confidence value to a 0..100 percentage for the DS meters.
 *
 * - canonical `0..1` → multiplied by 100 (the live-API path)
 * - a value already `> 1` → assumed to be a percentage, passed through
 *   (the mock-fixture path — no double-scaling)
 * - `null` / `undefined` / `NaN` → `0`
 *
 * The result is rounded to a whole percent and clamped to `[0, 100]`.
 */
export function toConfidencePercent(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) {
    return 0;
  }
  const percent = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(percent)));
}
